/**
 * Composes the tabs, the pool link, and the notifications row into one
 * `Dialog`, so the chat details panel exists as a single component the
 * route can open (WI-W10).
 *
 * RF-5 supersedes this item's original "exactly three tabs" decision: the
 * panel ships FOUR tabs, `Members` / `Media` / `Files` / `Pins`, in that
 * order.
 */
import {
  useChatDeleteForMe,
  useChatGet,
} from "@sportsmet/messaging-sdk/private";
import { confirmDestructive, Dialog, FOCUS_RING, Tabs } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  chatDetailsSubtitle,
  DELETE_CHAT_FOR_ME_LABEL,
  deleteChatForMeConfirmBody,
  deleteChatForMeConfirmTitle,
} from "./chatDetailsCopy";
import { FilesTab } from "./FilesTab";
import { MediaTab } from "./MediaTab";
import { MembersTab } from "./MembersTab";
import { MuteDialog } from "./MuteDialog";
import { muteHeaderLabel } from "./muteDisplay";
import { PinsTab } from "./PinsTab";

export type typeChatDetailsPanelProps = {
  chatId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tab: string;
  onTabChange: (next: string) => void;
  onJumpToMessage: (messageId: string) => void;
  /** ISSUE-1363. A private chat was deleted for this fan: leave it. */
  onDeletedForMe?: (() => void) | undefined;
};

type typeMuteLocalState = {
  muteState: "off" | "timed" | "indefinite";
  mutedUntil: string | null;
};

export const ChatDetailsPanel = (
  props: typeChatDetailsPanelProps,
): ReactNode => {
  const chatGet = useChatGet({ chatId: props.chatId }, { enabled: props.open });
  const chat = chatGet.data?.data;

  const [muteOverride, setMuteOverride] = useState<typeMuteLocalState | null>(
    null,
  );
  const [isMuteDialogOpen, setIsMuteDialogOpen] = useState(false);

  const muteState = muteOverride?.muteState ?? chat?.muteState ?? "off";
  const mutedUntil =
    muteOverride !== null
      ? muteOverride.mutedUntil
      : (chat?.mutedUntil ?? null);

  const deleteForMe = useChatDeleteForMe();

  const handleDeleteForMe = async (): Promise<void> => {
    if (chat === null || chat === undefined) return;
    const isConfirmed = await confirmDestructive({
      title: deleteChatForMeConfirmTitle(),
      body: deleteChatForMeConfirmBody(chat.title),
      confirmLabel: DELETE_CHAT_FOR_ME_LABEL,
    });
    if (!isConfirmed) return;
    try {
      const result = await deleteForMe.mutateAsync({ chatId: props.chatId });
      if (showApiError(result)) return;
      props.onDeletedForMe?.();
    } catch {
      showOfflineError();
    }
  };

  const notificationsValue =
    muteHeaderLabel(muteState, mutedUntil, new Date()) ?? "Every message";

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={chat?.title ?? "Chat details"}
      description={chat ? chatDetailsSubtitle(chat.memberCount) : undefined}
      autoFocus={false}
    >
      <div className="flex flex-col gap-4">
        {chat?.chatType === "pool" && chat.poolId ? (
          <Link
            to="/pools"
            search={{ poolId: chat.poolId }}
            data-testid="chat-details-go-to-pool"
            className="flex min-h-touch-target items-center text-body-strong text-primary underline-offset-2 hover:underline"
          >
            Go to the pool
          </Link>
        ) : null}

        <button
          type="button"
          data-testid="chat-details-notifications-row"
          className="flex min-h-touch-target w-full items-center justify-between gap-2 text-left"
          onClick={() => {
            setIsMuteDialogOpen(true);
          }}
        >
          <span className="text-body text-ink">Notifications</span>
          <span
            data-testid="chat-details-notifications-value"
            className="text-body text-ink-secondary"
          >
            {notificationsValue}
          </span>
        </button>

        <Tabs
          label="Chat details"
          value={props.tab}
          onValueChange={props.onTabChange}
          items={[
            {
              value: "members",
              label: "Members",
              content: (
                <MembersTab
                  chatId={props.chatId}
                  canModerate={chat?.canModerate ?? false}
                />
              ),
            },
            {
              value: "media",
              label: "Media",
              content: <MediaTab chatId={props.chatId} />,
            },
            {
              value: "files",
              label: "Files",
              content: <FilesTab chatId={props.chatId} />,
            },
            {
              value: "pins",
              label: "Pins",
              content: (
                <PinsTab
                  chatId={props.chatId}
                  canModerate={chat?.canModerate ?? false}
                  onJumpToMessage={props.onJumpToMessage}
                />
              ),
            },
          ]}
        />

        {chat?.chatType === "private" ? (
          <button
            type="button"
            data-testid="chat-details-delete-for-me"
            disabled={deleteForMe.isPending}
            aria-busy={deleteForMe.isPending}
            onClick={() => {
              void handleDeleteForMe();
            }}
            className={`flex min-h-touch-target w-full items-center text-left text-body-strong text-error disabled:opacity-60 ${FOCUS_RING}`}
          >
            {DELETE_CHAT_FOR_ME_LABEL}
          </button>
        ) : null}
      </div>

      <MuteDialog
        chatId={props.chatId}
        open={isMuteDialogOpen}
        onOpenChange={setIsMuteDialogOpen}
        isMuted={muteState !== "off"}
        onMuteChanged={setMuteOverride}
      />
    </Dialog>
  );
};
